import React, {useEffect} from 'react'
import {useTranslation} from 'react-i18next'
import {Form, Typography} from 'antd'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import {SendMessageRequest} from '@/api'
import {Input, IconTitle} from '@/components'
import {UK_PHONE_NUMBER_PREFIX} from '@/constants'
import {NOT_EMPTY_PATTERN, PHONE_UK_NUMBER_PATTERN} from '@/constants/patterns'
import {PayByLinkSendMessageStoreSymbol} from '@/pages/PayByLink'
import {SendMessageStore} from '@/stores/pay-by-link/SendMessageStore'
import {LinkShareFormProps} from './props'
import styles from './styles.scss'
import {RECIPIENT_EMAIL, RECIPIENT_PHONE} from '@/constants/playwright-ids'

const {Text} = Typography

export const LinkShareForm: React.FC<LinkShareFormProps> = observer(
    ({type, customer, email, formId, form, id, isMultiUse, senderNameHandler}) => {
        const {t} = useTranslation()

        const {sendMessage, merchantName} = useInjection<SendMessageStore>(
            PayByLinkSendMessageStoreSymbol
        )

        useEffect(() => {
            form.resetFields()
            senderNameHandler(merchantName)
        }, [form, senderNameHandler, merchantName])

        const onFinish = (values: SendMessageRequest) => {
            switch (type) {
                case 'email':
                    values.channel = 'email'
                    values.receiverContact = values.receiverContact.toLowerCase()
                    sendMessage(id, values, customer || values.receiverName || email)
                    form.resetFields()
                    break
                case 'sms':
                    values.channel = 'sms'
                    values.receiverContact = `${UK_PHONE_NUMBER_PREFIX}${values.receiverContact}`
                    sendMessage(id, values, customer || values.receiverName || email)
                    form.resetFields()
                    break
            }
        }

        const renderReceiverContact = () => {
            switch (type) {
                case 'email':
                    return (
                        <Form.Item
                            label={
                                !!customer
                                    ? t('LinkShareForm.recipientsEmail', {customer})
                                    : t('LinkShareForm.recipientEmail')
                            }
                            name='receiverContact'
                            rules={[
                                {
                                    required: true,
                                    message: t('LinkShareForm.errors.recipientEmailRequired')
                                },
                                {
                                    type: 'email',
                                    message: t('LinkShareForm.errors.recipientEmailInvalid')
                                }
                            ]}
                        >
                            <Input
                                data-test-id={RECIPIENT_EMAIL}
                                placeholder={t('LinkShareForm.recipientEmail')}
                            />
                        </Form.Item>
                    )
                case 'sms':
                    return (
                        <Form.Item
                            label={
                                !!customer
                                    ? `${customer}${t('LinkShareForm.recipientsNumber')}`
                                    : `${t('LinkShareForm.recipientNumber')}`
                            }
                            name='receiverContact'
                            rules={[
                                {
                                    required: true,
                                    message: t('LinkShareForm.errors.recipientNumberRequired')
                                },
                                {
                                    pattern: PHONE_UK_NUMBER_PATTERN,
                                    message: t('LinkShareForm.errors.recipientNumberInvalid')
                                }
                            ]}
                        >
                            <Input
                                placeholder={t('LinkShareForm.recipientNumber')}
                                maxLength={10}
                                prefix={
                                    <Text className={styles.prefix}>{UK_PHONE_NUMBER_PREFIX}</Text>
                                }
                                data-test-id={RECIPIENT_PHONE}
                            />
                        </Form.Item>
                    )
            }
        }

        return (
            <Form
                form={form}
                id={formId}
                layout={'vertical'}
                initialValues={{remember: true, senderName: merchantName}}
                onFinish={onFinish}
                autoComplete='off'
            >
                <Form.Item
                    label={t('LinkShareForm.recipientName')}
                    name='senderName'
                    rules={[
                        {required: true, message: t('LinkShareForm.errors.recipientNameRequired')},
                        {
                            pattern: NOT_EMPTY_PATTERN,
                            message: t('LinkShareForm.errors.recipientNameRequired')
                        }
                    ]}
                >
                    <Input
                        onChange={(event) => senderNameHandler(event.target.value)}
                        placeholder={t('LinkShareForm.recipientName')}
                    />
                </Form.Item>
                {renderReceiverContact()}

                {isMultiUse && (
                    <Form.Item
                        name='receiverName'
                        label={<IconTitle title={t('MultiUseForm.labels.receiverName')} />}
                        rules={[
                            {required: true, message: t('MultiUseForm.errors.receiverNameRequired')}
                        ]}
                    >
                        <Input placeholder={t('MultiUseForm.labels.receiverName')} />
                    </Form.Item>
                )}
            </Form>
        )
    }
)
